import { lockScroll, unlockScroll } from '@outdoor-voices/ui/dist/utils/dom/scroll';
import cc from 'classcat';
import React, { memo, useEffect, useRef, useState } from 'react';
import useKeyFocus from '@outdoor-voices/ui/dist/utils/hooks/useKeyFocus';

import { useLoyaltyHistoryQuery } from '../../generated/graphql';
import css from './progress-report.module.scss';
import overlayCSS from './overlay.module.scss';
import XLarge from '../icons/XLarge';
import PlusToMinus from './PlusToMinus';

let elementToReturnFocusTo = null;

const formatDate = (date: string): string => {
  const parts = date.split('-');
  return `${parts[1]}/${parts[2]}/${parts[0]}`;
};

type CollapserProps = {
  action?: string;
  date?: string;
  points?: number;
  status?: string;
  idx: number;
};
const Collapser: React.FC<CollapserProps> = ({ date, idx, action, points, status }) => {
  const [open, setOpen] = useState(false);

  const toggleOpen = () => {
    setOpen(!open);
  };

  const id = `collapser-${idx}`;
  const bgON = idx % 2 === 0;

  return (
    <>
      <button
        aria-controls={id}
        aria-expanded={open ? 'true' : 'false'}
        className={cc([css.collapser, { [css.bg]: bgON }])}
        id={`${id}-label`}
        onClick={toggleOpen}
        type="button"
      >
        {action}
        <PlusToMinus open={open} />
      </button>
      <div
        aria-labelledby={`${id}-label`}
        className={cc([css.collapserRegion, { [css.bg]: bgON }])}
        hidden={!open}
        id={id}
        role="region"
      >
        Date: {formatDate(date)}
        <br />
        Points: {points}
        <br />
        Status: {status}
      </div>
    </>
  );
};

const Report: React.FC = () => {
  const { data, loading } = useLoyaltyHistoryQuery();

  if (loading) {
    return <>Loading</>;
  }

  const { items } = data.loyaltyHistory;

  return (
    <>
      <ul className={css.list}>
        <li className={cc([css.item, css.header])}>
          <div id="column-date">Date</div>
          <div id="column-action">Action</div>
          <div id="column-points">Points</div>
          <div id="column-status">Status/Code</div>
        </li>
        {items.length === 0 && <li className={css.item}>Nothing yet.</li>}
        {items.map(({ date, action, points, status }, idx) => (
          <li className={css.item} key={idx}>
            <div aria-labelledby="column-date">{formatDate(date)}</div>
            <div aria-labelledby="column-action">{action}</div>
            <div aria-labelledby="column-points">{points}</div>
            <div aria-labelledby="column-status">{status}</div>
          </li>
        ))}
      </ul>
      <div className={css.collapsers}>
        {items.map((item, idx) => (
          <Collapser key={`collapser-${idx}`} idx={idx} {...item} />
        ))}
      </div>
    </>
  );
};

type Props = {
  white?: boolean;
};

const ProgressReport: React.FC<Props> = ({ white = false }) => {
  const overlayRef = useRef(null);
  const [opened, setOpened] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { open, setOpen } = useKeyFocus(false, overlayRef);

  const onClose = () => {
    setOpen(false);
  };

  const onOpen = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    setOpened(true);
    setOpen(true);
  };

  useEffect(() => {
    if (mounted) {
      return;
    }
    setMounted(true);
  }, [mounted]);

  useEffect(() => {
    if (open) {
      // Track the currently focused element so we can return to it on close.
      elementToReturnFocusTo = document.activeElement;
      lockScroll();
    } else {
      unlockScroll();
      if (elementToReturnFocusTo) {
        elementToReturnFocusTo.focus();
      }
    }
    return () => {
      unlockScroll();
    };
  }, [open]);

  if (!mounted) {
    return <></>;
  }

  const closeLabel = 'Close dialog';
  const tabIndex = open ? 0 : -1;

  return (
    <>
      <button
        aria-label="Click to view your progress report"
        className={cc([css.button, { [css.white]: white }])}
        onClick={onOpen}
      >
        Progress Report
      </button>
      <div aria-hidden={open ? 'false' : 'true'} ref={overlayRef}>
        <div
          aria-describedby="progress-report-details"
          aria-labelledby="progress-report-title"
          aria-modal="true"
          className={cc([overlayCSS.overlay, overlayCSS.wide, { [overlayCSS.on]: open }])}
          role="dialog"
        >
          <button
            aria-label={closeLabel}
            className={overlayCSS.close}
            onClick={onClose}
            tabIndex={tabIndex}
            title={closeLabel}
          >
            <XLarge />
          </button>
          <div className={css.content}>
            <h3 id="progress-report-title" className={css.title}>
              Progress Report
            </h3>
            {(open || opened) && <Report />}
          </div>
        </div>
        <button
          aria-label={closeLabel}
          className={cc([overlayCSS.bg, { [overlayCSS.on]: open }])}
          onClick={onClose}
          tabIndex={tabIndex}
          title={closeLabel}
        ></button>
      </div>
    </>
  );
};
export default memo(ProgressReport);
